import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Search, CheckCircle2, Plus, RefreshCw, X } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, HeroCard, Pill, SectionHeader, Eyebrow } from "@/components/dryvn";
import { ApiError } from "@/lib/api";
import type { Integration } from "@/data/types";
// The catalog is static product content. In LIVE mode the real connectors
// (V1, spec docs/specs/LIFE_CONNECTORS_2026-07.md) reflect backend status and
// their Connect starts the real OAuth flow. Every other tile stays
// "available" and disabled ("arriving later").
import {
  integrations as integrationsCatalog,
  integrationCategories as categories,
} from "@/data/mock";
import { IS_DEMO } from "@/lib/mode";
import {
  useConnectors,
  useConnect,
  useSyncConnector,
  useDisconnectConnector,
  useConnectICloud,
  useConnectPlaid,
  useExchangePlaid,
} from "@/lib/queries";

// Catalog tile id → backend connector id. Both are accepted by the connect/
// sync/disconnect routes via resolveProvider, and the backend status is keyed
// by the connector id. Every tile listed here is a real, live connector.
// Microsoft surfaces use the backend id verbatim (no short alias like gcal);
// one consent covers both, so connecting either flips both to connected.
const REAL_CONNECTORS: Record<string, string> = {
  gcal: "google_calendar",
  google_tasks: "google_tasks",
  google_drive: "google_drive",
  // Google Sheets rides the connected Google account via incremental consent
  // (like google_drive) — one Google consent covers it. Microsoft Excel rides
  // the Microsoft OAuth redirect (like microsoft_outlook_calendar/todo). Both
  // WRITE a "Dryvn Finance" sheet/workbook to the user's own account; the
  // connect flow is the same OAuth redirect (useConnect) as the tiles above.
  google_sheets: "google_sheets",
  microsoft_excel: "microsoft_excel",
  microsoft_outlook_calendar: "microsoft_outlook_calendar",
  microsoft_todo: "microsoft_todo",
  // Credential-form (iCloud) and Plaid-Link (bank) surfaces are real
  // connectors too, but they DON'T use the OAuth redirect — their Connect is
  // handled by ICloudConnect / PlaidConnect below, not useConnect.
  icloud_calendar: "icloud_calendar",
  plaid: "plaid",
};

// Tiles where a read_only grant can be upgraded to two-way by reconnecting
// with the write scope: Google Calendar (edits sync back) plus the finance
// write-out surfaces — Google Sheets and Microsoft Excel, which write a "Dryvn
// Finance" sheet/workbook to the user's own account and pull their edits back.
// Every other surface (Microsoft calendar/tasks, task lists, Spotify, YouTube)
// is import-only, so a read_only status there is the ceiling, not a prompt to
// reconnect.
const TWO_WAY_CAPABLE = new Set<string>(["gcal", "google_sheets", "microsoft_excel"]);

// The two-way label shown on a connected surface. Google Calendar edits update
// Google; Sheets/Excel keep the user's "Dryvn Finance" sheet/workbook in their
// own account up to date (write-out, with their edits pulled back). A two-way
// tile without an entry falls back to a plain line.
const TWO_WAY_LABEL: Record<string, string> = {
  gcal: "Two-way sync — your Dryvn edits update Google.",
  google_sheets: "Two-way sync — Dryvn keeps your “Dryvn Finance” sheet up to date.",
  microsoft_excel: "Two-way sync — Dryvn keeps your “Dryvn Finance” workbook up to date.",
};

// The backend returns { message, code } on failure. Map the codes we know to
// an honest, plain sentence; fall back to a generic honest line.
function apiErrorCode(error: unknown): string | undefined {
  if (
    error instanceof ApiError &&
    error.body &&
    typeof error.body === "object" &&
    "code" in error.body
  ) {
    return String((error.body as { code: unknown }).code);
  }
  return undefined;
}

function connectorErrorMessage(error: unknown): string {
  switch (apiErrorCode(error)) {
    case "OAUTH_UNCONFIGURED":
      return "Google connection isn't configured on this server yet.";
    case "CONNECTOR_NOT_AVAILABLE":
      return "This connection is arriving later.";
    case "NOT_CONNECTED":
      return "This isn't connected yet — connect it first.";
    case "NOT_PROVISIONED":
      return "Connections aren't switched on for your account yet.";
    default:
      return "That didn't go through just now — please try again in a moment.";
  }
}

// iCloud credential-form failures. The backend returns { message, code }:
// CONNECTOR_AUTH_FAILED (bad Apple ID / app password), MISSING_CREDENTIALS
// (empty field), CONNECTOR_NOT_PROVISIONED (server not switched on).
function icloudErrorMessage(error: unknown): string {
  switch (apiErrorCode(error)) {
    case "CONNECTOR_AUTH_FAILED":
      return "Apple didn't accept that — check the Apple ID and app-specific password and try again.";
    case "MISSING_CREDENTIALS":
      return "Enter both your Apple ID and an app-specific password.";
    case "CONNECTOR_NOT_PROVISIONED":
      return "iCloud isn't switched on for this account yet.";
    default:
      return "That didn't go through just now — please try again in a moment.";
  }
}

// Plaid Link script (CDN). Injected once, lazily, on the first bank connect —
// react-plaid-link isn't a dependency. Resolves to window.Plaid.
type PlaidHandler = { open: () => void; exit: () => void };
type PlaidLink = {
  create: (config: {
    token: string;
    onSuccess: (
      publicToken: string,
      metadata: { institution?: { name?: string } | null },
    ) => void;
    onExit: (err: unknown) => void;
  }) => PlaidHandler;
};
declare global {
  interface Window {
    Plaid?: PlaidLink;
  }
}

let plaidScriptPromise: Promise<PlaidLink> | null = null;
function loadPlaid(): Promise<PlaidLink> {
  if (typeof window !== "undefined" && window.Plaid) return Promise.resolve(window.Plaid);
  if (plaidScriptPromise) return plaidScriptPromise;
  plaidScriptPromise = new Promise<PlaidLink>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";
    script.async = true;
    script.onload = () => {
      if (window.Plaid) resolve(window.Plaid);
      else reject(new Error("plaid_unavailable"));
    };
    script.onerror = () => {
      plaidScriptPromise = null; // allow a retry on the next click
      reject(new Error("plaid_script_failed"));
    };
    document.head.appendChild(script);
  });
  return plaidScriptPromise;
}

// After the OAuth round-trip the backend 302s back here with either
// ?connected=<provider family> or ?error=<code>. Both are one-shot: we show an
// honest notice, then strip the param so a refresh doesn't replay it.
const PROVIDER_LABEL: Record<string, string> = {
  google: "Google",
  microsoft: "Microsoft",
  spotify: "Spotify",
};

function redirectConnectedMessage(provider: string): string {
  const label = PROVIDER_LABEL[provider];
  return label
    ? `${label} connected — syncing now.`
    : "Connected — syncing now.";
}

function redirectErrorMessage(code: string): string {
  switch (code) {
    case "declined":
      return "You declined the permission — you can start again anytime.";
    case "invalid_state":
      return "That connection attempt expired before it finished — please try connecting again.";
    case "no_token":
      return "The provider didn't hand back access — please try connecting again.";
    case "try_again":
      return "Something hiccupped — try connecting again in a moment.";
    case "unconfigured":
      return "This connection isn't set up on the server yet.";
    case "not_provisioned":
      return "Connections aren't fully turned on for this account yet.";
    default:
      return "Something went wrong finishing that connection — please try again in a moment.";
  }
}

// Sync + Disconnect actions for a connected tile. Own state per card so a
// sync result / disconnect confirm on one tile never bleeds into another.
function ConnectorActions({ id }: { id: string }) {
  const sync = useSyncConnector();
  const disconnect = useDisconnectConnector();
  const [confirming, setConfirming] = useState(false);

  const note = sync.isError
    ? connectorErrorMessage(sync.error)
    : disconnect.isError
      ? connectorErrorMessage(disconnect.error)
      : sync.isSuccess
        ? sync.data.status === "action-needed"
          ? "Synced, but this connection needs attention — try reconnecting."
          : `Synced just now — ${sync.data.imported} added, ${sync.data.updated} updated.`
        : null;
  const noteIsError = sync.isError || disconnect.isError;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <button
          onClick={() => sync.mutate(id)}
          disabled={sync.isPending || disconnect.isPending}
          className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] disabled:opacity-50"
          style={{ borderColor: "var(--dryvn-line)", color: "var(--dryvn-ink)" }}
        >
          <RefreshCw className="h-3 w-3" /> {sync.isPending ? "Syncing…" : "Sync now"}
        </button>
        {confirming ? (
          <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: "var(--dryvn-grey)" }}>
            Disconnect?
            <button
              onClick={() => {
                setConfirming(false);
                disconnect.mutate(id);
              }}
              className="rounded-full px-2 py-0.5 font-medium"
              style={{ color: "var(--dryvn-warm-deep)" }}
            >
              Yes
            </button>
            <button onClick={() => setConfirming(false)} className="rounded-full px-2 py-0.5">
              No
            </button>
          </span>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            disabled={sync.isPending || disconnect.isPending}
            className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] disabled:opacity-50"
            style={{ borderColor: "var(--dryvn-line)", color: "var(--dryvn-grey)" }}
          >
            {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
          </button>
        )}
      </div>
      {note && (
        <p
          className="text-[11px]"
          style={{ color: noteIsError ? "var(--dryvn-warm-deep)" : "var(--dryvn-grey)" }}
        >
          {note}
        </p>
      )}
    </div>
  );
}

// iCloud connect: an inline credential form (Apple ID + app-specific
// password), shown when the user clicks Connect on the iCloud tile. Owns its
// own open/field/error state so it never bleeds into another tile.
function ICloudConnect() {
  const connect = useConnectICloud();
  const [open, setOpen] = useState(false);
  const [appleId, setAppleId] = useState("");
  const [appPassword, setAppPassword] = useState("");

  if (connect.isSuccess) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-medium"
        style={{ color: "var(--dryvn-primary-deep)" }}
      >
        <CheckCircle2 className="h-3 w-3" /> Connected — importing now.
      </span>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-white"
        style={{ backgroundColor: "var(--dryvn-primary)" }}
      >
        <Plus className="h-3 w-3" /> Connect
      </button>
    );
  }

  const canSubmit = appleId.trim().length > 0 && appPassword.trim().length > 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        connect.mutate({ appleId: appleId.trim(), appPassword: appPassword.trim() });
      }}
      className="flex w-full flex-col gap-2"
    >
      <input
        type="email"
        value={appleId}
        onChange={(e) => setAppleId(e.target.value)}
        placeholder="Apple ID (email)"
        autoComplete="username"
        className="w-full rounded-lg border px-2.5 py-1.5 text-[12px] outline-none"
        style={{
          borderColor: "var(--dryvn-line)",
          color: "var(--dryvn-ink)",
          backgroundColor: "var(--dryvn-card)",
        }}
      />
      <input
        type="password"
        value={appPassword}
        onChange={(e) => setAppPassword(e.target.value)}
        placeholder="App-specific password"
        autoComplete="off"
        className="w-full rounded-lg border px-2.5 py-1.5 text-[12px] outline-none"
        style={{
          borderColor: "var(--dryvn-line)",
          color: "var(--dryvn-ink)",
          backgroundColor: "var(--dryvn-card)",
        }}
      />
      <p className="text-[11px] leading-snug" style={{ color: "var(--dryvn-grey)" }}>
        Generate an app-specific password at appleid.apple.com (Sign-In &amp; Security → App-Specific
        Passwords) — not your real Apple password.
      </p>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={connect.isPending || !canSubmit}
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: "var(--dryvn-primary)" }}
        >
          {connect.isPending ? "Connecting…" : "Connect"}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-full px-2.5 py-1 text-[11px]"
          style={{ color: "var(--dryvn-grey)" }}
        >
          Cancel
        </button>
      </div>
      {connect.isError && (
        <p className="text-[11px]" style={{ color: "var(--dryvn-warm-deep)" }}>
          {icloudErrorMessage(connect.error)}
        </p>
      )}
    </form>
  );
}

// Plaid connect: fetch a link token, open Plaid Link, then exchange the
// public token the widget hands back. No OAuth redirect — the whole flow is
// client-driven from this tile's Connect button.
function PlaidConnect() {
  const connectPlaid = useConnectPlaid();
  const exchangePlaid = useExchangePlaid();
  const [phase, setPhase] = useState<"idle" | "opening" | "exchanging" | "done">("idle");
  const [error, setError] = useState<string | null>(null);

  async function start() {
    setError(null);
    setPhase("opening");
    try {
      const res = await connectPlaid.mutateAsync();
      if (!res.link_token) throw new Error("no_link_token");
      const Plaid = await loadPlaid();
      const handler = Plaid.create({
        token: res.link_token,
        onSuccess: (publicToken, metadata) => {
          setPhase("exchanging");
          exchangePlaid.mutate(
            { public_token: publicToken, institution_name: metadata?.institution?.name },
            {
              onSuccess: () => setPhase("done"),
              onError: () => {
                setPhase("idle");
                setError("That didn't finish linking — please try again.");
              },
            },
          );
        },
        onExit: (err) => {
          setPhase("idle");
          if (err) setError("Plaid closed before finishing — you can try again.");
        },
      });
      handler.open();
    } catch {
      setPhase("idle");
      setError("Couldn't open the bank connection just now — please try again in a moment.");
    }
  }

  if (phase === "done") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-medium"
        style={{ color: "var(--dryvn-primary-deep)" }}
      >
        <CheckCircle2 className="h-3 w-3" /> Connected — syncing now.
      </span>
    );
  }

  const busy = phase === "opening" || phase === "exchanging";
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        onClick={start}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-white disabled:opacity-50"
        style={{ backgroundColor: "var(--dryvn-primary)" }}
      >
        <Plus className="h-3 w-3" /> {busy ? "Connecting…" : "Connect"}
      </button>
      {error && (
        <p className="text-[11px] text-right" style={{ color: "var(--dryvn-warm-deep)" }}>
          {error}
        </p>
      )}
    </div>
  );
}

// One-shot params the OAuth redirect lands with. Kept as loose strings — the
// message mappers own the known-value → copy translation, so an unknown value
// still resolves to an honest fallback line rather than throwing.
type IntegrationsSearch = { connected?: string; error?: string };

export const Route = createFileRoute("/more/settings/integrations")({
  // Un-gated in live (2026-07-15): Google Calendar and Google Tasks have real
  // backends, so the catalog renders in live mode. Other tiles stay disabled.
  validateSearch: (search: Record<string, unknown>): IntegrationsSearch => ({
    connected: typeof search.connected === "string" ? search.connected : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Integrations — Dryvn Life" },
      {
        name: "description",
        content: "Connect Dryvn Life to the services your life already runs on.",
      },
    ],
  }),
  component: IntegrationsPage,
});

function statusPill(s: Integration["status"]) {
  if (s === "connected")
    return (
      <Pill tone="life">
        <CheckCircle2 className="mr-1 h-3 w-3" /> Connected
      </Pill>
    );
  if (s === "action-needed") return <Pill tone="warm">Action needed</Pill>;
  if (s === "coming-soon") return <Pill tone="muted">Coming soon</Pill>;
  return <Pill tone="muted">Available</Pill>;
}

function IntegrationsPage() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<(typeof categories)[number]>("All");
  const connectorsQuery = useConnectors();
  const connect = useConnect();

  // Consume the OAuth redirect params (?connected / ?error). Capture the notice
  // into state, refetch connectors on success so the tile flips, then strip the
  // params from the URL (replace, no reload) so a refresh doesn't replay it.
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const [redirectNotice, setRedirectNotice] = useState<
    { tone: "success" | "error"; text: string } | null
  >(null);

  useEffect(() => {
    if (!search.connected && !search.error) return;
    if (search.connected) {
      setRedirectNotice({ tone: "success", text: redirectConnectedMessage(search.connected) });
      // Tile status lives in the connectors query — refetch so it flips to connected.
      void qc.invalidateQueries({ queryKey: ["connectors"] });
    } else if (search.error) {
      setRedirectNotice({ tone: "error", text: redirectErrorMessage(search.error) });
    }
    void navigate({ search: {}, replace: true });
  }, [search.connected, search.error, navigate, qc]);

  // In demo, the catalog shows its designed statuses. In live, each real
  // connector carries its backend status (keyed by connector id); every other
  // tile reads "available".
  const integrations: Integration[] = useMemo(() => {
    if (IS_DEMO) return integrationsCatalog;
    const connectors = connectorsQuery.data;
    return integrationsCatalog.map((i) => {
      const backendId = REAL_CONNECTORS[i.id];
      if (backendId) {
        const status = connectors?.find((c) => c.id === backendId)?.status;
        return { ...i, status: status ?? "available", meta: undefined };
      }
      // Not a built connector: honest "coming soon", NOT a connectable
      // "available". Only the 6 real connectors above can actually connect.
      return { ...i, status: "coming-soon" as const, meta: undefined };
    });
  }, [connectorsQuery.data]);

  // Honest sync capability per connected tile: two_way (Dryvn edits reach
  // Google) vs read_only (needs a reconnect to gain the write scope). Present
  // only for a connected surface, so absent in demo and on unconnected tiles.
  const syncModeByTile = useMemo(() => {
    const m = new Map<string, "two_way" | "read_only">();
    for (const [tileId, backendId] of Object.entries(REAL_CONNECTORS)) {
      const mode = connectorsQuery.data?.find((c) => c.id === backendId)?.syncMode;
      if (mode) m.set(tileId, mode);
    }
    return m;
  }, [connectorsQuery.data]);

  const filtered = integrations.filter((i) => {
    if (cat !== "All" && i.category !== cat) return false;
    if (q && !`${i.name} ${i.desc}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const connected = integrations.filter((i) => i.status === "connected");
  const needsAction = integrations.filter((i) => i.status === "action-needed");

  return (
    <AppShell>
      <Link
        to="/more/settings"
        className="mb-4 inline-flex items-center gap-1 text-sm"
        style={{ color: "var(--dryvn-grey)" }}
      >
        <ChevronLeft className="h-4 w-4" /> Settings
      </Link>

      {redirectNotice && (
        <div
          className="mb-4 flex items-start justify-between gap-3 rounded-xl px-3.5 py-2.5 text-sm"
          style={
            redirectNotice.tone === "success"
              ? {
                  backgroundColor: "color-mix(in oklab, var(--dryvn-life) 22%, transparent)",
                  color: "var(--dryvn-primary-deep)",
                }
              : {
                  backgroundColor: "color-mix(in oklab, var(--dryvn-warm) 26%, transparent)",
                  color: "var(--dryvn-warm-deep)",
                }
          }
        >
          <span className="min-w-0">{redirectNotice.text}</span>
          <button
            onClick={() => setRedirectNotice(null)}
            className="shrink-0 rounded-full p-0.5 opacity-70 hover:opacity-100"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <HeroCard
        eyebrow="Integrations"
        title={
          <>
            The stack that{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              fuels
            </em>{" "}
            you.
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone="life">{connected.length} connected</Pill>
          {needsAction.length > 0 && <Pill tone="warm">{needsAction.length} needs attention</Pill>}
          <Pill tone="muted">{integrations.length - connected.length} available</Pill>
        </div>
      </HeroCard>

      {needsAction.length > 0 && (
        <div className="mt-8">
          <SectionHeader title="Needs attention" eyebrow="Action" />
          <div className="grid gap-3 sm:grid-cols-2">
            {needsAction.map((i) => (
              <DCard key={i.id} className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <span
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
                    style={{
                      backgroundColor: "color-mix(in oklab, var(--dryvn-warm) 30%, transparent)",
                    }}
                  >
                    <i.Icon className="h-5 w-5" style={{ color: "var(--dryvn-warm-deep)" }} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                      {i.name}
                    </p>
                    <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                      {i.meta}
                    </p>
                  </div>
                  {/* Reconnect re-runs the OAuth consent flow — the same path
                      as Connect (buildLifeConsentUrl issues a fresh grant). */}
                  <button
                    onClick={() => connect.mutate(i.id)}
                    disabled={connect.isPending}
                    className="rounded-full px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
                    style={{ backgroundColor: "var(--dryvn-warm-deep)" }}
                  >
                    {connect.isPending && connect.variables === i.id ? "Reconnecting…" : "Reconnect"}
                  </button>
                </div>
                {connect.isError && connect.variables === i.id && (
                  <p className="text-[11px]" style={{ color: "var(--dryvn-warm-deep)" }}>
                    {connectorErrorMessage(connect.error)}
                  </p>
                )}
              </DCard>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <SectionHeader title="Browse" eyebrow="All integrations" />

        <div
          className="mb-3 flex items-center gap-2 rounded-full border px-3 py-2"
          style={{ borderColor: "var(--dryvn-line)", backgroundColor: "var(--dryvn-card)" }}
        >
          <Search className="h-4 w-4" style={{ color: "var(--dryvn-grey)" }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search integrations"
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: "var(--dryvn-ink)" }}
          />
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {categories.map((c) => {
            const active = c === cat;
            return (
              <button
                key={c}
                onClick={() => setCat(c)}
                className="rounded-full px-3 py-1 text-xs font-medium transition"
                style={{
                  backgroundColor: active ? "var(--dryvn-primary)" : "var(--dryvn-page-tint)",
                  color: active ? "#fff" : "var(--dryvn-ink)",
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((i) => (
            <DCard key={i.id} className="flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <span
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
                  style={{
                    backgroundColor: "color-mix(in oklab, var(--dryvn-life) 30%, transparent)",
                  }}
                >
                  <i.Icon className="h-5 w-5" style={{ color: "var(--dryvn-primary-deep)" }} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                      {i.name}
                    </p>
                    {statusPill(i.status)}
                  </div>
                  <Eyebrow>{i.category}</Eyebrow>
                </div>
              </div>
              <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                {i.desc}
              </p>
              {i.privacyNote && (
                <p
                  className="rounded-lg px-2.5 py-1.5 text-[11px] leading-snug"
                  style={{
                    backgroundColor: "color-mix(in oklab, var(--dryvn-life) 20%, transparent)",
                    color: "var(--dryvn-primary-deep)",
                  }}
                >
                  {i.privacyNote}
                </p>
              )}
              <div className="mt-auto flex items-center justify-between">
                <span className="text-[11px] font-mono" style={{ color: "var(--dryvn-grey)" }}>
                  {i.meta ?? "\u00A0"}
                </span>
                {i.status === "connected" ? (
                  <div className="flex flex-col items-end gap-2">
                    <ConnectorActions id={i.id} />
                    {syncModeByTile.get(i.id) === "two_way" && (
                      <p
                        className="text-[11px] text-right"
                        style={{ color: "var(--dryvn-grey)" }}
                      >
                        {TWO_WAY_LABEL[i.id] ?? "Two-way sync."}
                      </p>
                    )}
                    {syncModeByTile.get(i.id) === "read_only" &&
                      (TWO_WAY_CAPABLE.has(i.id) ? (
                        <div className="flex flex-col items-end gap-1">
                          {/* Reconnect re-runs OAuth and requests the write scope
                              (same path as Connect) — no separate endpoint. */}
                          <button
                            onClick={() => connect.mutate(i.id)}
                            disabled={connect.isPending && connect.variables === i.id}
                            className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] disabled:opacity-50"
                            style={{
                              borderColor: "var(--dryvn-line)",
                              color: "var(--dryvn-primary-deep)",
                            }}
                          >
                            {connect.isPending && connect.variables === i.id
                              ? "Reconnecting…"
                              : "Reconnect to enable two-way sync"}
                          </button>
                          {connect.isError && connect.variables === i.id && (
                            <p
                              className="text-[11px] text-right"
                              style={{ color: "var(--dryvn-warm-deep)" }}
                            >
                              {connectorErrorMessage(connect.error)}
                            </p>
                          )}
                        </div>
                      ) : (
                        // Import-only surface (Microsoft, task lists): read-only
                        // is the ceiling — no two-way to upgrade to yet.
                        <p className="text-[11px] text-right" style={{ color: "var(--dryvn-grey)" }}>
                          Read-only import
                        </p>
                      ))}
                  </div>
                ) : (
                  (() => {
                    // Real connectors (REAL_CONNECTORS) start the OAuth
                    // redirect via useConnect; every other tile is disabled.
                    const isRealConnector = !IS_DEMO && i.id in REAL_CONNECTORS;
                    // iCloud and Plaid are real but don't use the OAuth
                    // redirect — they own their Connect UI (credential form /
                    // Plaid Link). In demo they fall through to the inert
                    // designed button below, like every other tile.
                    if (isRealConnector && i.id === "icloud_calendar") return <ICloudConnect />;
                    if (isRealConnector && i.id === "plaid") return <PlaidConnect />;
                    const enabled = IS_DEMO || isRealConnector;
                    const connecting =
                      isRealConnector && connect.isPending && connect.variables === i.id;
                    // A live tile that isn't a built connector shows an honest
                    // "Coming soon" chip, never a Connect button it can't honor.
                    if (!enabled) {
                      return (
                        <span
                          className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium"
                          style={{ backgroundColor: "var(--dryvn-hairline)", color: "var(--dryvn-grey)" }}
                        >
                          Coming soon
                        </span>
                      );
                    }
                    return (
                      <div className="flex flex-col items-end gap-1">
                        <button
                          disabled={!enabled || connecting}
                          onClick={isRealConnector ? () => connect.mutate(i.id) : undefined}
                          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-white disabled:opacity-50"
                          style={{ backgroundColor: "var(--dryvn-primary)" }}
                        >
                          <Plus className="h-3 w-3" /> {connecting ? "Connecting…" : "Connect"}
                        </button>
                        {connect.isError && connect.variables === i.id && (
                          <p
                            className="text-[11px] text-right"
                            style={{ color: "var(--dryvn-warm-deep)" }}
                          >
                            {connectorErrorMessage(connect.error)}
                          </p>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
            </DCard>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="mt-6 text-center text-sm" style={{ color: "var(--dryvn-grey)" }}>
            No integrations match “{q}”.
          </p>
        )}
      </div>
    </AppShell>
  );
}
